import { forwardRef, useImperativeHandle, useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { UserPaymentMethod, PaymentMethod } from '@finpay-payment-v3/payment-types';

import { createSetupIntent } from '../services/payment-service';
import { savePaymentMethod, createACHPaymentMethod } from '../services/payment-method-service';
import PaymentHubCardForm, { CardPaymentRef } from './payment-hub-card-form';
import PaymentHubACHForm, { ACHPaymentRef } from './payment-hub-ach-form';
import PaymentMethodList from './payment-method-list';
import PaymentSourceTabs, { RECURRING_PAYMENT_OPTIONS, PaymentSourceType } from './payment-source-tabs';
import { AddressDetails } from './guarantor-address-fields';

export interface RecurringPaymentMethodSectionProps {
  savedPaymentMethods: UserPaymentMethod[];
  billingDetails: {
    firstName: string;
    lastName: string;
    email: string;
  };
  onBillingDetailsChange: (field: string, value: string) => void;
  isAddingNewChampion?: boolean;
  showAddressFields?: boolean;
  addressDetails?: AddressDetails;
  onAddressDetailsChange?: (field: keyof AddressDetails, value: string) => void;
  initialSelectedMethodId?: number | null;
  initialPaymentSource?: 'CARD' | 'ACH';
  showEmailField?: boolean;
  onDeleteMethod?: (methodId: number) => Promise<void>;
  deletingMethodId?: number | null;
}

export interface CreatePaymentMethodResult {
  success: boolean;
  paymentMethodId: number | null;
  last4?: string;
  error?: string;
}

export interface RecurringPaymentMethodSectionRef {
  getSelectedMethodId: () => number | null;
  isAddingNew: () => boolean;
  getPaymentSource: () => 'CARD' | 'ACH';
  getCardFormRef: () => CardPaymentRef | null;
  getAchFormRef: () => ACHPaymentRef | null;
  getSetupIntentId: () => string | null;
  createAndSavePaymentMethod: (params: { userId: number; authToken: string | null | undefined; stripeCustomerId?: string }) => Promise<CreatePaymentMethodResult>;
  reset: () => void;
}

const RecurringPaymentMethodSection = forwardRef<RecurringPaymentMethodSectionRef, RecurringPaymentMethodSectionProps>(
  ({ savedPaymentMethods, billingDetails, onBillingDetailsChange, isAddingNewChampion = false, showAddressFields = false, addressDetails, onAddressDetailsChange, initialSelectedMethodId, initialPaymentSource, showEmailField = true, onDeleteMethod, deletingMethodId }, ref) => {
    const [paymentSource, setPaymentSource] = useState<'CARD' | 'ACH'>(initialPaymentSource || 'CARD');
    const [selectedMethodId, setSelectedMethodId] = useState<number | null>(initialSelectedMethodId ?? null);
    const [isAddingNewMethod, setIsAddingNewMethod] = useState(false);
    const [setupIntentClientSecret, setSetupIntentClientSecret] = useState<string | null>(null);
    const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
    const [isSetupIntentLoading, setIsSetupIntentLoading] = useState(false);
    const cardFormRef = useRef<CardPaymentRef>(null);
    const achFormRef = useRef<ACHPaymentRef>(null);
    
    const createAndSaveCardPaymentMethod = useCallback(async (
      userId: number,
      authToken: string | null | undefined,
      stripeCustomerId?: string
    ): Promise<CreatePaymentMethodResult> => {
      const cardRef = cardFormRef.current;
      if (!cardRef) {
        return { success: false, paymentMethodId: null, error: 'Card form not initialized' };
      }

      if (!setupIntentId) {
        return { success: false, paymentMethodId: null, error: 'Setup intent not initialized' };
      }

      const createResult = await cardRef.createPaymentMethod();
      if (!createResult.success || !createResult.paymentMethodId) {
        return { success: false, paymentMethodId: null, error: createResult.message || 'Failed to create card payment method' };
      }

      try {
        const savedMethod = await savePaymentMethod({
          userId,
          extPaymentMethodId: createResult.paymentMethodId,
          extSetupIntentId: setupIntentId,
          ...(stripeCustomerId && { extPaymentUserId: stripeCustomerId }),
          paymentMethodTypeId: PaymentMethod.Card,
          last4: createResult.last4 || '',
          cardBrand: createResult.brand || '',
          expMonth: createResult.expMonth,
          expYear: createResult.expYear,
          authToken,
        });

        return { success: true, paymentMethodId: savedMethod.userPaymentMethodId, last4: createResult.last4 || '' };
      } catch (err: any) {
        return { success: false, paymentMethodId: null, error: err?.message || 'Failed to save card payment method' };
      }
    }, [setupIntentId]);

    const createAndSaveAchPaymentMethod = useCallback(async (
      userId: number,
      authToken: string | null | undefined,
      stripeCustomerId?: string
    ): Promise<CreatePaymentMethodResult> => {
      const achRef = achFormRef.current;
      if (!achRef) {
        return { success: false, paymentMethodId: null, error: 'ACH form not initialized' };
      }

      const isValid = achRef.validate();
      if (!isValid) {
        return { success: false, paymentMethodId: null, error: 'Please complete the ACH form' };
      }

      const bankDetails = achRef.getBankDetails();
      const achBillingDetails = achRef.getBillingDetails();

      const achResult = await createACHPaymentMethod({
        routingNumber: bankDetails.routingNumber,
        accountNumber: bankDetails.accountNumber,
        billingDetails: {
          name: `${achBillingDetails.firstName} ${achBillingDetails.lastName}`.trim(),
          email: achBillingDetails.email,
        },
      });

      if (!achResult.success || !achResult.paymentMethodId) {
        return { success: false, paymentMethodId: null, error: achResult.message || 'Failed to create ACH payment method' };
      }

      try {
        const savedMethod = await savePaymentMethod({
          userId,
          extPaymentMethodId: achResult.paymentMethodId,
          extSetupIntentId: setupIntentId || undefined,
          ...(stripeCustomerId && { extPaymentUserId: stripeCustomerId }),
          paymentMethodTypeId: PaymentMethod.BankAccount,
          last4: achResult.last4 || '',
          cardBrand: 'ach',
          isVerified: true,
          authToken,
        });

        return { success: true, paymentMethodId: savedMethod.userPaymentMethodId, last4: achResult.last4 || '' };
      } catch (err: any) {
        return { success: false, paymentMethodId: null, error: err?.message || 'Failed to save ACH payment method' };
      }
    }, [setupIntentId]);

    useImperativeHandle(ref, () => ({
      getSelectedMethodId: () => isAddingNewMethod ? null : selectedMethodId,
      isAddingNew: () => isAddingNewMethod,
      getPaymentSource: () => paymentSource,
      getCardFormRef: () => cardFormRef.current,
      getAchFormRef: () => achFormRef.current,
      getSetupIntentId: () => setupIntentId,
      createAndSavePaymentMethod: async (params: { userId: number; authToken: string | null | undefined; stripeCustomerId?: string }) => {
        if (paymentSource === 'CARD') {
          return createAndSaveCardPaymentMethod(params.userId, params.authToken, params.stripeCustomerId);
        } else {
          return createAndSaveAchPaymentMethod(params.userId, params.authToken, params.stripeCustomerId);
        }
      },
      reset: () => {
        setSelectedMethodId(null);
        setIsAddingNewMethod(false);
        setSetupIntentClientSecret(null);
        setSetupIntentId(null);
      },
    }), [isAddingNewMethod, selectedMethodId, paymentSource, setupIntentId, createAndSaveCardPaymentMethod, createAndSaveAchPaymentMethod]);

    // Update state when initial values change (e.g., when existing recurring method is loaded)
    useEffect(() => {
      if (initialSelectedMethodId !== undefined && initialSelectedMethodId !== null) {
        setSelectedMethodId(initialSelectedMethodId);
        setIsAddingNewMethod(false);
      }
    }, [initialSelectedMethodId]);

    useEffect(() => {
      if (initialPaymentSource) {
        setPaymentSource(initialPaymentSource);
      }
    }, [initialPaymentSource]);
    
    const handlePaymentSourceChange = (newSource: 'CARD' | 'ACH') => {
      setPaymentSource(newSource);
      // When switching back to the initial payment source, restore the initial selection
      if (newSource === initialPaymentSource && initialSelectedMethodId) {
        setSelectedMethodId(initialSelectedMethodId);
        setIsAddingNewMethod(false);
      } else {
        setSelectedMethodId(null);
        setIsAddingNewMethod(false);
      }
      setSetupIntentClientSecret(null);
    };
    
    const handleSelectMethod = (methodId: number) => {
      setSelectedMethodId(methodId);
      setIsAddingNewMethod(false);
    };
    
    const handleAddNew = async () => {
      // Prevent duplicate calls if already loading or already adding
      if (isSetupIntentLoading || isAddingNewMethod) {
        return;
      }
      
      setIsAddingNewMethod(true);
      setSelectedMethodId(null);
      setIsSetupIntentLoading(true);
      setSetupIntentClientSecret(null);
      
      const paymentMethodTypeId = paymentSource === 'ACH' ? PaymentMethod.BankAccount : PaymentMethod.Card;
      const result = await createSetupIntent({ paymentMethodTypeId });
      
      if (result.success && result.clientSecret && result.setupIntentId) {
        setSetupIntentClientSecret(result.clientSecret);
        setSetupIntentId(result.setupIntentId);
      }
      
      setIsSetupIntentLoading(false);
    };
    
    const listSelectedMethodId = isAddingNewMethod ? 'new' : (selectedMethodId?.toString() ?? null);
    
    const handleListMethodSelect = (methodIdStr: string) => {
      if (methodIdStr === 'new') {
        handleAddNew();
      } else {
        handleSelectMethod(parseInt(methodIdStr, 10));
      }
    };
    
    return (
      <>
        <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }} test-id="recurring-payment-method-section">
          <Typography variant="subtitle1" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            Recurring Payment Method
          </Typography>
        </Grid>
        <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', py: 0.5 }}>
          <Box display="flex" justifyContent="flex-end" sx={{ width: '100%' }}>
            <Box sx={{ width: 'auto', minWidth: 200 }} test-id="recurring-payment-source-tabs">
              <PaymentSourceTabs
                value={paymentSource}
                onChange={(newSource) => handlePaymentSourceChange(newSource as 'CARD' | 'ACH')}
                options={RECURRING_PAYMENT_OPTIONS}
                name="recurringPaymentSource"
                minWidth={200}
              />
            </Box>
          </Box>
        </Grid>
        
        <Grid item xs={12}>
          <PaymentMethodList
            savedPaymentMethods={savedPaymentMethods}
            paymentSource={paymentSource}
            selectedMethodId={listSelectedMethodId}
            onMethodSelect={handleListMethodSelect}
            showDefaultChip={false}
            onDeleteMethod={onDeleteMethod ? async (methodId) => {
              await onDeleteMethod(methodId);
              if (selectedMethodId === methodId) {
                setSelectedMethodId(null);
                setIsAddingNewMethod(true);
              }
            } : undefined}
            deletingMethodId={deletingMethodId}
          />

          {isAddingNewMethod && paymentSource === 'CARD' && (
            <Box sx={{ mt: 2, border: '1px solid #e0e0e0', borderRadius: 1, p: 2, backgroundColor: '#fafafa' }}>
              {isSetupIntentLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Loading payment form...
                  </Typography>
                </Box>
              ) : (
                <PaymentHubCardForm
                  key={`recurring-card-form-${setupIntentClientSecret}`}
                  ref={cardFormRef}
                  isActive={isAddingNewMethod && paymentSource === 'CARD'}
                  clientSecret={setupIntentClientSecret}
                  isLoading={isSetupIntentLoading}
                  billingDetails={billingDetails}
                  onBillingDetailsChange={onBillingDetailsChange}
                  showNameFields={isAddingNewChampion}
                  showAddressFields={showAddressFields}
                  addressDetails={addressDetails}
                  onAddressDetailsChange={onAddressDetailsChange}
                  showEmailField={showEmailField}
                />
              )}
            </Box>
          )}

          {isAddingNewMethod && paymentSource === 'ACH' && (
            <Box sx={{ mt: 2 }} test-id="recurring-ach-form-container">
              <PaymentHubACHForm
                ref={achFormRef}
                isActive={isAddingNewMethod && paymentSource === 'ACH'}
                billingDetails={billingDetails}
                onBillingDetailsChange={onBillingDetailsChange}
                showNameFields={isAddingNewChampion}
                savedBankAccounts={[]}
                showAddressFields={showAddressFields}
                addressDetails={addressDetails}
                onAddressDetailsChange={onAddressDetailsChange}
                showEmailField={showEmailField}
              />
            </Box>
          )}
        </Grid>
      </>
    );
  }
);

RecurringPaymentMethodSection.displayName = 'RecurringPaymentMethodSection';

export default RecurringPaymentMethodSection;
